import {
  ISdkService,
  OpenGameOptions,
  UserData
} from './sdk.service.interface';
import {computed, makeObservable} from 'mobx';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {
  DeviceInfo,
  GetAutoLoginTokenResponse,
  ICgpSdk,
  CgpSkinInfo,
  ISdkRegulationService,
  CmsUserSignature, ITokenInfo
} from './cgp-sdk.interface';
import {ServiceBase} from '../ServiceBase';
import {IRunningGameHandler} from '../games/running-game-handler.interface';
import {SdkEvents} from './sdk-events';
import {Check, TimeSpan} from '@sparkware/app-utils';
import {CGP_OBJECT_NAME} from '../../app-bootstrap/constants';
import {IAutoLoginParams} from '../../../common/interfaces/autologin-params.interface';
import IStructuredLoggingService from "../../../common/interfaces/structured-logging-service-interface";
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import {IGeoLocationInfo} from "../../../common/interfaces/geo-location-info.interface";
import {ISdkHybridService} from "../hybrid/hybrid.service.interface";
import {ILegacyRegulation} from "../regulation/legacy-regulation.interface";
import {SdkEventsTrigger} from "./sdk-events-trigger";
import {IScriptLoaderService} from "../scrip-loader/script-loader.service.interface";
import IStateOfResidence from "../../../common/interfaces/state-of-residence.interface";
import {GNWP_SOURCES} from "../gnwp/gnwp-consts";
import {NullableString} from '../../../common/types/nullable-types';
import {LoadScriptOptions} from "../../../common/interfaces/load-script-options";
import {IInactivityTimeoutSettings} from "../user/model/inactivity-timeout-settings";
import {SdkOverlayZIndex} from "../../../common/interfaces/sdk-overlay-z-index";
import {ExternalLinksZIndexes} from "../external-links/external-links-z-indexes";
import {GameRawData} from "./gme-raw-data.type";

export class SdkService extends ServiceBase implements ISdkService {
  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);


    //setTimeout to avoid circular dependency
    //SdkService (constructor)
    // => OAuth2AnonymousState (_onRunAutologin)
    // => OAuth2BackendProxy (_loginMethod is calling isMigrationEnabledFromConfig from PlayerMigrationService)
    // => PlayerMigrationService (isMigrationEnabledFromConfig)
    // => ConfigurationService (servicesConfigs is calling SdkService.getConfigs())
    // => SdkService - and here because the call was initiated in the SdkConstructor the ServiceFactory will execute again teh SdkService constructor.
    setTimeout(() => {
      this._updateLocalStorageAccordingToCookie();
    });


    this._displayHybrid();

    this.reaction(() => this.services.location.currentUri,
      () => {
        if (this.services.location.hasNavigationHistory) {
          this.CGP.routeChanged();
        }
      });
  }

  private _displayHybrid() {
    //F TO DO why is this here and not in the SDK ?
    if (Check.isFunction(this.CGP.displayHybrid)) {
      this.CGP.displayHybrid();
    }
  }

  private _events: SdkEvents = null;

  get events(): SdkEvents {
    if (!this._events) {
      this._events = new SdkEvents(this.CGP);
    }

    return this._events;
  }


  private get CGP(): ICgpSdk {
    return this.services.window.getGlobalProperty(CGP_OBJECT_NAME) as ICgpSdk;
  }


  get isAuthorized(): boolean {
    return this.CGP.isAuthorized();
  }

  get isLoginFromRegistration(): boolean {
    return this.CGP.isLoginFromRegistration();
  }

  get getCGPMaxOverlayZIndex(): number {
    return 9002;
  }

  get structuredLoggingService(): IStructuredLoggingService {
    return this.CGP.structuredLoggingService();
  }

  get userData(): UserData {
    let rawUserData = this.CGP.getUserData();
    let rawAccountData = (rawUserData && rawUserData.accountData) || {};


    return {
      userName: rawUserData?.userName,
      firstName: rawAccountData.FirstName,
      lastName: rawAccountData.LastName,
      IsVIP: this.CGP.isVIP(),
      statusGroupId: rawAccountData.StatusGroupId,
      GenderId: rawAccountData.genderID,
      timeSinceRegistration: rawAccountData.TimeSinceRegistration,
      cid: rawUserData?.cid,
      playerRMStatus: rawAccountData.PlayerRMStatus,
      sessionId: this.CGP.getSessionId(),
      initialSessionId: this.CGP.getInitialSessionId(),
      registrationCountry: rawAccountData.RegistrationCountry,
      joinDate: rawAccountData.JoinDate,
      isFTD: !Check.isNullOrUndefined(rawAccountData.FTDSubBrand),
      IsPromotionPermitted: rawAccountData.IsPromotionPermitted,
      taxId: rawAccountData.TaxId,
      lastLogin: rawAccountData.LastLogin,
      lastLoginDateInTimeZone: rawAccountData.LastLoginDateInTimeZone,
      mobileNumber: rawAccountData.MobileNumber,
      realityCheckInterval: Check.isNullOrUndefined(rawAccountData.RealityCheckDataIntervalInMins) ? null : TimeSpan.fromMinutes(rawAccountData.RealityCheckDataIntervalInMins)
    };
  }

  sendAnalytics(category: string, action: string = null, label: string = null, value: string = null, omitStructuredLogger: boolean = null) {
    try {
      this.CGP.sendAnalytics({
        category: category,
        action: action,
        label: label,
        value: value,
        omitStructuredLogger: omitStructuredLogger
      });
    } catch (ex) {
      this.services.logger.error(`SdkService failed to sendAnalytics: ${ex}`);
    }
  }

  getCmsUserSignature(): CmsUserSignature {
    return this.CGP.getUserSegments();
  }

  formatMoney(value: number, withDecimal: boolean = false): string {
    return this.CGP.format$$$(value, withDecimal);
  }

  get device(): DeviceInfo {
    return this.CGP.device();
  }

  waitForAutologin(): Promise<void> {
    return this.CGP.waitForAutologin();
  }

  async openGame(options: OpenGameOptions): Promise<IRunningGameHandler> {

    let openGameOptions: any = {...options};
    if (!options.showRegister) {
      openGameOptions.navbar = { //F TO DO - do we realy need this navbar option ?
        login: {
          showRegister: false
        }
      }
    }

    return await this.CGP.openGame(openGameOptions);

  }

  async openLoginForm(marketingParams?: MarketingParams): Promise<void> {

    await this.CGP.openLoginForm(marketingParams);
  }

  getGamesInfoForHistory(gameTypes: number[]): Promise<GameRawData> {
    return this.CGP.getGamesInfoForHistory(gameTypes);
  }

  getGamesInfo(gameTypes: number[]): Promise<GameRawData> {
    return this.CGP.getGamesInfo(gameTypes);
  }

  getGamesThemes(): Record<number, string> {
    return this.CGP.getGamesThemes();
  }

  getGamesVendors(): Record<number, string> {
    return this.CGP.getGamesVendors();
  }

  getGamesCompanies(): Record<number, string> {
    return this.CGP.getGamesCompanies();
  }

  getAutoLoginToken(): Promise<GetAutoLoginTokenResponse> {
    return this.CGP.getAutoLoginToken();
  }

  logout(): Promise<void> {
    return this.CGP.logout();
  }

  getBrandID(): number {
    return this.CGP.getBrandID();
  }

  getSubBrandID(): number {
    return this.CGP.getSubBrandID();
  }

  getRegulationService(): ISdkRegulationService {
    return this.CGP.getRegulationService();
  }

  isHybridEmbeddedGame(gameType: number): boolean {
    return this.CGP.isHybridEmbeddedGame(gameType);
  }

  openHybridLink(url: string): void {
    this.CGP.openHybridLink(url);
  }

  private _updateLocalStorageAccordingToCookie() {
    try {
      let autologinCookie = this.services.cookies.getCookie('orbit_autologin_token');

      if (autologinCookie) {
        this.CGP.setAutoLoginToken(autologinCookie);
      }
    } catch (err) {
      this.services.logger.error('update local storage according to cookie failed. Error: ' + err);
    } finally {
      // Remove cookie
      this.services.cookies.removeCookie('orbit_autologin_token');
    }

  }

  getGRSEnvironmentUrls(): Promise<any> {
    return this.CGP.getGRSEnvironments();
  }

  @computed
  get activeChallengeCount(): number {
    return this.CGP.countPlayerChallenges();
  }

  executeMenuAction(id: string): void {
    this.CGP.menuActionExecute(id);
  }

  menuActionExecute(key: string): void {
    return this.CGP.menuActionExecute(key);
  }


  startLiveTableFeeds(): void {
    if (Check.isFunction(this.CGP.startLiveTableFeeds)) {
      this.CGP.startLiveTableFeeds();
    } else {
      //TODO - to be removed in the future
      (this.CGP as any).startEvolutionLiveFeeds();
    }
  }

  stopLiveTableFeeds(): void {
    if (Check.isFunction(this.CGP.stopLiveTableFeeds)) {
      this.CGP.stopLiveTableFeeds();
    } else {
      //TODO - to be removed in the future
      (this.CGP as any).stopEvolutionLiveFeeds();
    }
  }

  runAutoLogin(autoLoginParams: IAutoLoginParams) {
    return this.CGP.runAutoLogin(autoLoginParams);
  }


  getProductPackageId(): number {
    return this.CGP.getProductPackage();
  }

  getSkin(): CgpSkinInfo {
    return {
      getPlatformSystemID: () => this.CGP.getSkin().getPlatformSystemID(),
      getClientPlatform: () => this.CGP.getSkin().getClientPlatform(),
      getVersion: () => this.CGP.getSkin().getVersion(),
      getCulture: () => this.CGP.getSkin().getCulture(),
      getBrandName: () => this.CGP.getSkin().getBrandName(),
      getMinimumAge: () => this.CGP.getSkin().getMinimumAge(),
      getSerialData: (marketingData: any)=> this.CGP.getSkin().getSerialData(marketingData),
      getDefaultHybridSerial: () => this.CGP.getSkin().getDefaultHybridSerial()
    };
  }


  fireOrbitInitComplete(): void {
    this.CGP.onOrbitInitComplete();
  }

  getEnvironment(): string {
    return this.CGP.getEnvironment();
  }

  pushDataToDataLayer(data: {}): void {
    this.CGP.pushDataToDataLayer(data);
  }

  getCountryName() {
    return this.CGP.getCountryName();
  }

  getCountryIso3(): string {
    return this.CGP.getCountryIso3();
  }

  getSupportedStates(): IStateOfResidence[] {
    return this.CGP.getSupportedStates();
  }

  getWrongLocationDetectionPath(): string {
    return this.CGP.getWrongLocationDetectionPath();
  }

  httpPost<TResponse>(url: string, requestData: unknown): Promise<TResponse> {
    return this.CGP.httpPost<TResponse>(url, requestData);
  }

  httpGet<TResponse>(url: string, requestData: unknown): Promise<TResponse> {
    return this.CGP.httpGet<TResponse>(url, requestData);
  }

  getConfigs(): any {
    return this.CGP.getConfigs();
  }

  loadScript(options: LoadScriptOptions): Promise<void> {
    return this.CGP.loadScript(options);
  }

  getRefreshToken(): string {
    return this.CGP.getRefreshToken();
  }

  getCurrencyIso3(): string {
    return this.CGP.getCurrencyIso3();
  }

  get overlayZIndex(): SdkOverlayZIndex {
    return {
      OMG: ExternalLinksZIndexes.Omg,
      GAME_FRAME: ExternalLinksZIndexes.GameFrame
    };
  }

  getGoogleClientID(): string {
    return this.CGP.getGoogleClientID();
  }

  getGoogleSessionID(): string {
    return this.CGP.getGoogleSessionID();
  }

  isInAutologin(): boolean {
    return this.CGP.isInAutologin();
  }

  fireDepositWidgetClosed(): void {
    this.CGP.fireDepositWidgetClosed();
  }  
  
  fireFreeToPlayWidgetClosed(): void {
    this.CGP.fireFreeToPlayWidgetClosed();
  }

  get tokenInfo(): ITokenInfo | null {
    return this.CGP.getToken() || null;
  }

  getGeoLocationInfo(): Promise<IGeoLocationInfo | null> {
    return this.CGP.getGeoLocationInfo();
  }

  getDownloadAppLink(): string {
    return this.CGP.getDownloadAppLink();
  }

  isDepositWidgetSupportedByHybrid(): boolean {
    return this.CGP.isDepositWidgetSupportedByHybrid();
  }

  promptPushNotificationPermissionRequest() {
    return this.CGP.promptPushNotificationPermissionRequest();
  }

  openPopUpOnSecondWebView(content: string, timeout: number): void {
    this.CGP.openPopUpOnSecondWebView(content, timeout);
  }

  closePopUpOnSecondWebView(): void {
    this.CGP.closePopUpOnSecondWebView();
  }

  get hybrid(): ISdkHybridService {
    return this.CGP.hybrid;
  }

  get currentRegulation(): ILegacyRegulation {
    return this.CGP.currentRegulation;
  }

  eventsTrigger: SdkEventsTrigger = new SdkEventsTrigger();

  getCurrentBrowserTabId(): string {
    return this.CGP.getCurrentBrowserTabId();
  }

  get scriptLoader(): IScriptLoaderService {
    return this.CGP.scriptLoader;
  }

  startAutoRetryGeoProcess(): Promise<void> {
    return this.CGP.startAutoRetryGeoProcess();
  }

  extractTokenProperty(tokenProperty: string) {
    return this.CGP.extractTokenProperty(tokenProperty);
  }

  updateTokenProperty(tokenProperty: string, value: NullableString): void {
    this.CGP.updateTokenProperty(tokenProperty, value);
  }

  isLoginPopupEnabled(): boolean {
    return this.CGP.isLoginPopupEnabled();
  }

  getAccountData(): any {
    return this.CGP.getAccountData();
  }

  getTempAccountGnwpSource(): GNWP_SOURCES {
    return this.CGP.getTempAccountGnwpSource();
  }

  getInactivityTimeoutSettings(): IInactivityTimeoutSettings {
    return this.CGP.getInactivityTimeoutSettings();
  }

  setInactivityTimeout(timeout: TimeSpan): void {
    this.CGP.setInactivityTimeout(timeout)
  }

  createUserActivityDetactor(params){
    return this.CGP.createUserActivityDetactor(params);
  }

  async writeWidgetLogs(payload: any): Promise<void> {
    await this.CGP.writeWidgetLogs(payload);
  }

  getFakeClientIP(): NullableString {
    return this.CGP.getFakeClientIP();
  }
}